"use client";

import React from "react";

const variantClasses = {
  success: "bg-statusSucces",
  danger: "bg-statusDanger",
  warning: "bg-statusWarning",
  info: "bg-statusInfo",
  primary: "bg-lightCyan",
};

/**
 * ProgressBar - Horizontal progress indicator.
 *
 * Supports stacking by nesting ProgressBar children:
 *   <ProgressBar>
 *     <ProgressBar variant="success" now={30} />
 *     <ProgressBar variant="danger" now={20} />
 *   </ProgressBar>
 *
 * @param {number} now - Current progress value
 * @param {number} min - Minimum value (default 0)
 * @param {number} max - Maximum value (default 100)
 * @param {string} variant - 'success' | 'danger' | 'warning' | 'info' | 'primary'
 * @param {string} label - Text displayed on the bar
 * @param {boolean} striped - Striped visual style
 * @param {boolean} animated - Animated stripe
 */
function ProgressBarComponent({
  now = 0,
  min = 0,
  max = 100,
  variant = "primary",
  label,
  striped = false,
  animated = false,
  className = "",
  children,
  isChild = false,
  ...props
}) {
  const percentage = Math.max(
    0,
    Math.min(100, ((now - min) / (max - min)) * 100)
  );
  const bgClass = variantClasses[variant] || variantClasses.primary;
  const stripedClass = striped ? "progress-bar-striped" : "";
  const animatedClass = animated ? "progress-bar-animated" : "";

  const stripedBg = striped
    ? {
        backgroundImage:
          "linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)",
        backgroundSize: "1rem 1rem",
      }
    : {};

  const bar = (
    <div
      className={`progress-bar ${bgClass} ${stripedClass} ${animatedClass} flex flex-col justify-center overflow-hidden text-white text-center whitespace-nowrap text-xs transition-[width] duration-500 ease`.trim()}
      role="progressbar"
      style={{
        width: `${percentage}%`,
        ...stripedBg,
      }}
      aria-valuenow={now}
      aria-valuemin={min}
      aria-valuemax={max}
    >
      {label}
    </div>
  );

  if (isChild) return bar;

  if (children) {
    return (
      <div
        className={`progress flex h-4 overflow-hidden rounded bg-lightGrey2 text-xs ${className}`.trim()}
        {...props}
      >
        {React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(child, { isChild: true })
            : child
        )}
      </div>
    );
  }

  return (
    <div
      className={`progress flex h-4 overflow-hidden rounded bg-lightGrey2 text-xs ${className}`.trim()}
      {...props}
    >
      {bar}
    </div>
  );
}

export const ProgressBar = ProgressBarComponent;
export default ProgressBarComponent;
